import type { ReactNode } from 'react';
import React from 'react';
import { t } from 'Src/i18n';
import { getFormattedArrivalDate, getFormattedDepartureDate } from 'Utils/proposal/proposalUtils';
import { StaySummaryTicketUpsell } from 'Components/widgets/stay-summary-ticket/StaySummaryTicketUpsell';
import { BeTransportDetails } from 'Components/widgets/be-transport-details/BeTransportDetails';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { ChildcareSummary } from 'Components/widgets/childcare-summary/ChildcareSummary';
import { GetParticipantsActivities } from 'Components/widgets/stay-summary-ticket/GetParticipantsActivities';
import { ICONS } from 'Components/utils/icon/icons';
import { getNumberOfAdultsAndChildren } from 'Utils/participants/participants.utils';
import { roomQuantity } from 'Utils/accommodation/accommodation.utils';
import { displayChildcaresNames } from 'Utils/childcare/childcare.utils';
import { displayActivitiesAndEquipmentsNames } from 'Utils/activity/activity.utils';
import type { ResortAccommodationLabel } from 'Src/types/reservation';
type Props = {
  accommodationSummary: ReactNode;
  locale: string;
  proposal: Proposal;
};
export const getTransferLabels = (proposal: Proposal) => {
  const firstTransfer = proposal.additionalServices.transfers?.[0];
  const { startDate: firstTransferStartDate, endDate: firstTransferEndDate } =
    firstTransfer?.pricingInfo?.schedules?.[0] || {};
  const isOneWayTransfer = firstTransfer?.pricingInfo?.schedules?.length === 1;
  const outboundOnlyIncluded =
    isOneWayTransfer && firstTransferStartDate === proposal.departureDate;
  const inboundOnlyIncluded = isOneWayTransfer && firstTransferEndDate === proposal.arrivalDate;
  let transferLabels: ReactNode = (
    <React.Fragment>
      {t('common.transport.transfer.roundTrip')}
      <br />
      {t('common.transport.transfer.arrivalPoint')}
    </React.Fragment>
  );

  if (outboundOnlyIncluded) {
    transferLabels = t('common.transport.transfer.outboundOnlyIncluded');
  }

  if (inboundOnlyIncluded) {
    transferLabels = t('common.transport.transfer.inboundOnlyIncluded');
  }

  return transferLabels;
};
export const getStaySummaryLinesByProposal = ({
  accommodationSummary,
  locale,
  proposal,
}: Props) => {
  const { accommodations, attendees, duration, price, resort } = proposal;
  const quantityOfAdults = attendees.adults.length;
  const quantityOfChildren = attendees.children.length;
  const childcares = proposal?.additionalServices?.childcares;
  const childcaresUpsell = childcares.filter(
    (childcare) => childcare.pricingInfo.isPartiallyRented === true,
  );
  const activities = proposal?.additionalServices?.activities?.filter((obj) => {
    return obj.pricingInfo.isPartiallyRented === true;
  });
  const equipments = proposal?.additionalServices?.rentals?.filter((rental) => {
    return rental.pricingInfo.isPartiallyRented === true;
  });
  const transfers = proposal?.additionalServices?.transfers;
  const transferUpsell = transfers?.filter((obj) => {
    return obj.pricingInfo.isFullyRented === true;
  });
  const isTransferIncluded =
    proposal.transports?.inbound?.transferIncluded ||
    proposal.transports?.outbound?.transferIncluded ||
    transfers?.some((obj) => obj.pricingInfo.isIncluded === true) ||
    false;
  const sumPriceAllActivities =
    price.excursions.value +
    price.sports.value +
    price.wellness.value +
    equipments
      .map((equipment) => equipment.pricingInfo.rentedAmount.value)
      .reduce((acc, currentValue) => acc + currentValue, 0);
  const hasTransports = proposal.transports !== null;
  const hasActivities = activities.length > 0;
  const hasEquipments = equipments.length > 0;
  const hasTransfer = transferUpsell.length > 0 || hasTransports;
  const accommodationsIds = accommodations.map((accommodation) => accommodation.id);
  const selectedAccommodations = (
    resort.accommodations as ReadonlyArray<ResortAccommodationLabel>
  ).filter((acc) => accommodationsIds.includes(acc.id));
  const bedroomsNames =
    selectedAccommodations.length > 1
      ? selectedAccommodations.map((el) => el.label).join(` ${t('common.and')} `)
      : selectedAccommodations[0]?.label;
  const transferLabels = getTransferLabels(proposal);
  const lines = [
    {
      iconName: ICONS.DATE,
      topLeft: t('booking.common.dates'),
      bottomLeft: `${getFormattedArrivalDate(proposal).slice(0, 6)} -
            ${getFormattedDepartureDate(proposal).slice(0, 6)}`,
      bottomRight:
        duration > 1
          ? t('booking.confirmation.nights', {
              duration,
            })
          : `${duration} ${t('booking.confirmation.night')}`,
      isFirstItem: true,
    },
    {
      iconName: ICONS.PEOPLE_DOUBLE,
      topLeft: t('booking.common.participants'),
      bottomLeft: getNumberOfAdultsAndChildren(quantityOfChildren, quantityOfAdults),
    },
    {
      iconName: ICONS.ACCOMODATIONS,
      bottomLeft: bedroomsNames,
      children: accommodationSummary,
      labelGA: 'receipt_room',
      isOpenedByDefault: true,
      topLeft:
        roomQuantity(accommodations)[0] > 1 || selectedAccommodations.length > 1
          ? t('booking.confirmation.rooms')
          : t('booking.confirmation.room'),
      trackingData: {
        payload: {
          detail_click: 'receipt_room',
        },
      },
    },
    {
      iconName: ICONS.TRANSFER,
      isNull: !hasTransfer,
      bottomLeft: transferLabels,
      labelGA: 'receipt_transfert',
      topLeft: t('common.transport.transfer.withTransferLabel'),
      topRight: (transferUpsell.length > 0 || isTransferIncluded) && (
        <StaySummaryTicketUpsell
          transferUpsell={transferUpsell}
          isTransferIncluded={isTransferIncluded}
          price={price}
          childcares={[]}
        />
      ),
      trackingData: {
        payload: {
          detail_click: 'receipt_transfert',
        },
      },
    },
    {
      iconName: ICONS.TRANSPORT,
      isNull: !hasTransports,
      bottomLeft: t('booking.confirmation.inboundAndOutbound'),
      topLeft: t('common.transport.withTransportLabel'),
      isOpenedByDefault: true,
      labelGA: 'receipt_transport',
      children: (
        <div className="mb-15">
          <BeTransportDetails transport={proposal?.transports} withoutTitle />
        </div>
      ),
      trackingData: {
        payload: {
          detail_click: 'receipt_transport',
        },
      },
    },
    {
      iconName: ICONS.KIDS,
      isNull: childcaresUpsell.length === 0,
      bottomLeft: displayChildcaresNames(proposal, childcaresUpsell),
      children: <ChildcareSummary childcares={childcaresUpsell} />,
      dataTestId: 'childcare-button',
      topLeft: t('common.price.childcareLabel'),
      topRight: childcares.length > 0 && (
        <StaySummaryTicketUpsell
          transferUpsell={transferUpsell}
          childcares={childcares}
          isTransferIncluded={isTransferIncluded}
          price={price}
        />
      ),
      trackingData: {
        payload: {
          detail_click: 'receipt_childcare',
        },
      },
    },
    {
      iconName: ICONS.TENNIS,
      isNull: !hasActivities && !hasEquipments,
      bottomLeft: displayActivitiesAndEquipmentsNames(
        hasActivities,
        hasEquipments,
        activities,
        equipments,
      ),
      bottomRight:
        '+' +
        formatAmountForLocale(sumPriceAllActivities, locale, undefined, {
          precision: 2,
        }),
      children: (
        <div className="mt-30">
          <GetParticipantsActivities
            proposal={proposal}
            participants={proposal.attendees.adults}
            isChild={false}
          />
          <GetParticipantsActivities
            proposal={proposal}
            participants={proposal.attendees.children}
            isChild
          />
        </div>
      ),
      dataTestId: 'activities-button',
      topLeft: t('common.activities.label'),
      topRight: (
        <p className="text-14 font-bold text-orange">{t('booking.confirmation.onDemand')}</p>
      ),
      trackingData: {
        payload: {
          detail_click: 'receipt_activities',
        },
      },
    },
  ];
  return lines;
};
